import styled, { keyframes } from "styled-components";
import { getColor, getSize, getSizeInRem, responsive } from "~/helpers/style";
import { sidebarWidth } from "~/components/layout/Sidebar.style";

/* ── Banner (sticky bottom-left) ── */

const slideUp = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

export const BannerWrapper = styled.div`
  position: fixed;
  bottom: ${getSize(2.4)};
  left: calc(${sidebarWidth}px + ${getSize(2.4)});
  z-index: 999;
  width: 360px;
  background: #1a1a2e;
  border-radius: ${getSize(1.2)};
  padding: ${getSize(2.0)};
  color: #ffffff;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  animation: ${slideUp} 0.4s ease-out;

  ${responsive("tablet", true)} {
    width: calc(100% - ${getSize(4.8)});
  }
`;

export const CloseButton = styled.button`
  position: absolute;
  top: ${getSizeInRem(1.2)};
  right: ${getSizeInRem(1.2)};
  background: none;
  border: none;
  color: #ffffff;
  cursor: pointer;
  opacity: 1;
  padding: ${getSizeInRem(0.4)};
  font-size: ${getSizeInRem(1.6)};
  line-height: 1;
  transition: opacity 0.2s;

  &:disabled {
    color: #555;
    cursor: default;
    opacity: 0.3;
  }

  &:not(:disabled):hover {
    opacity: 0.8;
  }
`;

export const BannerTitle = styled.h3`
  margin: 0 0 ${getSize(0.6)};
  font-size: ${getSize(1.6)};
  font-weight: 700;
  color: #ffffff;
  padding-right: ${getSize(2.4)};
`;

export const BannerDescription = styled.p`
  margin: 0 0 ${getSize(1.6)};
  font-size: ${getSize(1.3)};
  line-height: 1.5;
  color: #b0b0c8;
`;

export const ClaimButton = styled.button`
  width: 100%;
  padding: ${getSize(1.2)} ${getSize(2.0)};
  border: none;
  border-radius: ${getSize(0.8)};
  background: #ffffff;
  color: #1a1a2e;
  font-size: ${getSize(1.4)};
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s ease-in-out;

  &:hover {
    filter: brightness(0.95);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

/* ── Confirmation Modal ── */

export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1001;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(8, 14, 40, 0.55);
  padding: ${getSize(2.4)};
`;

export const ModalCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${getSize(1.2)};
  width: min(480px, 100%);
  padding: ${getSize(3.2)};
  border-radius: ${getSize(1.6)};
  background: #ffffff;
  text-align: left;
`;

export const ModalCloseButton = styled.button`
  position: absolute;
  top: ${getSize(1.6)};
  right: ${getSize(1.6)};
  background: none;
  border: none;
  color: ${getColor("textSecondary")};
  cursor: pointer;
  padding: ${getSize(0.4)};
  font-size: ${getSize(1.8)};
  line-height: 1;

  &:hover {
    color: ${getColor("text")};
  }
`;

export const ModalIcon = styled.div`
  display: none;
`;

export const ModalTitle = styled.h2`
  margin: 0;
  color: ${getColor("text")};
  font-size: ${getSize(2.0)};
  font-weight: 700;
  line-height: 1.3;
`;

export const ModalDescription = styled.p`
  margin: 0;
  color: ${getColor("textSecondary")};
  font-size: ${getSize(1.4)};
  line-height: 1.6;
`;

export const Disclaimer = styled.p`
  margin: 0;
  color: ${getColor("textSecondary")};
  font-size: ${getSize(1.2)};
  font-style: italic;
`;

/* ── Testimonial ── */

export const TestimonialBox = styled.div`
  width: 100%;
  background: ${getColor("background")};
  border-radius: ${getSize(0.8)};
  padding: ${getSize(1.4)} ${getSize(1.6)};
  margin-top: ${getSize(0.4)};
  display: flex;
  align-items: center;
  gap: ${getSize(1.2)};
`;

export const TestimonialText = styled.p`
  margin: 0;
  color: ${getColor("text")};
  font-size: ${getSize(1.3)};
  font-style: italic;
  line-height: 1.5;
`;

/* ── Card display ── */

export const CardRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${getSize(0.8)};
  width: 100%;
  padding: ${getSize(1.2)} ${getSize(1.6)};
  border: 1px solid ${getColor("border")};
  border-radius: ${getSize(0.8)};
`;

export const ChangeCardLink = styled.a`
  margin-left: auto;
  font-size: ${getSize(1.3)};
  font-weight: 600;
  color: #E8556D;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

export const CardIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${getColor("textSecondary")};
`;

export const CardLabel = styled.span`
  display: block;
  font-size: ${getSize(1.1)};
  color: ${getColor("textSecondary")};
  margin-bottom: ${getSize(0.2)};
`;

export const CardDigits = styled.span`
  display: block;
  font-size: ${getSize(1.3)};
  font-weight: 600;
  color: ${getColor("text")};
  letter-spacing: 1px;
`;

/* ── Modal actions ── */

export const ModalActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${getSize(1.0)};
  width: 100%;
  margin-top: ${getSize(0.8)};
`;

export const ConfirmButton = styled.button`
  width: 100%;
  padding: ${getSize(1.4)} ${getSize(2.4)};
  border: none;
  border-radius: ${getSize(0.8)};
  background: #6056EB;
  color: #ffffff;
  font-size: ${getSize(1.5)};
  font-weight: 700;
  cursor: pointer;
  transition: filter 0.1s ease-in-out;

  &:hover {
    filter: brightness(0.95);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const CancelButton = styled.button`
  width: 100%;
  padding: ${getSize(1.2)} ${getSize(2.4)};
  border: 1px solid ${getColor("border")};
  border-radius: ${getSize(0.8)};
  background: #ffffff;
  color: ${getColor("text")};
  font-size: ${getSize(1.4)};
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.1s ease-in-out;

  &:hover {
    filter: brightness(0.98);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
